import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Save, X } from 'lucide-react';
import { formatCurrency } from '@/lib/currency';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CurrencySelect } from '@/components/ui/currency-select';
import { Skeleton } from '@/components/ui/skeleton';
import { useCashInflow, useUpdateCashInflow, useDeleteCashInflow, INFLOW_CATEGORY_LABELS, InflowCategory } from '@/hooks/finance/useCashInflows';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/finance/DeleteConfirmationDialog';
import { BusinessUnitSelect } from '@/components/finance/BusinessUnitSelect';
import { DEFAULT_BUSINESS_UNIT, BUSINESS_UNIT_LABELS } from '@/constants/businessUnits';
import { format, parseISO } from 'date-fns';

const CashInflowDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin';

  const { data: inflow, isLoading } = useCashInflow(id || '');
  const { data: bankAccounts } = useBankAccounts(true);
  const updateInflow = useUpdateCashInflow();
  const deleteInflow = useDeleteCashInflow();

  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    inflow_date: '',
    category: 'capital_injection' as InflowCategory,
    amount: '',
    currency: 'ETB',
    bank_account_id: '',
    description: '',
    reference_number: '',
    source_name: '',
    business_unit: DEFAULT_BUSINESS_UNIT as string,
  });

  useEffect(() => {
    if (inflow) {
      setFormData({
        inflow_date: inflow.inflow_date,
        category: inflow.category,
        amount: inflow.amount.toString(),
        currency: inflow.currency || 'ETB',
        bank_account_id: inflow.bank_account_id || '',
        description: inflow.description || '',
        reference_number: inflow.reference_number || '',
        source_name: inflow.source_name || '',
        business_unit: (inflow as any).business_unit || DEFAULT_BUSINESS_UNIT,
      });
    }
  }, [inflow]);

  const handleSave = () => {
    if (!id) return;
    updateInflow.mutate({
      id,
      inflow_date: formData.inflow_date,
      category: formData.category,
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      bank_account_id: formData.bank_account_id || null,
      description: formData.description || null,
      reference_number: formData.reference_number || null,
      source_name: formData.source_name || null,
      business_unit: formData.business_unit,
    } as any, {
      onSuccess: () => setIsEditing(false),
    });
  };

  const handleDelete = () => {
    if (!id) return;
    deleteInflow.mutate(id, {
      onSuccess: () => navigate('/finance/accounting/cash-inflows'),
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!inflow) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold">Cash inflow not found</h2>
          <Button className="mt-4" onClick={() => navigate('/finance/accounting/cash-inflows')}>Back to Cash Inflows</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/finance/accounting/cash-inflows')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{inflow.inflow_number}</h1>
              <p className="text-muted-foreground">Cash Inflow Details</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}><X className="h-4 w-4 mr-2" />Cancel</Button>
                <Button onClick={handleSave} disabled={updateInflow.isPending}><Save className="h-4 w-4 mr-2" />Save</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)}><Edit className="h-4 w-4 mr-2" />Edit</Button>
                {isAdmin && (
                  <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
                )}
              </>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Details</span>
              <Badge variant="secondary">{INFLOW_CATEGORY_LABELS[inflow.category]}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={formData.inflow_date} onChange={(e) => setFormData(prev => ({ ...prev, inflow_date: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v as InflowCategory }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(Object.entries(INFLOW_CATEGORY_LABELS) as [InflowCategory, string][]).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <CurrencySelect value={formData.currency} onValueChange={(v) => setFormData(prev => ({ ...prev, currency: v }))} className="w-full" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Bank Account</Label>
                  <Select value={formData.bank_account_id} onValueChange={(v) => setFormData(prev => ({ ...prev, bank_account_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select bank account" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts?.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Business Unit</Label>
                  <BusinessUnitSelect
                    value={formData.business_unit as any}
                    onValueChange={(v: any) => setFormData(prev => ({ ...prev, business_unit: v }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Source Name</Label>
                  <Input value={formData.source_name} onChange={(e) => setFormData(prev => ({ ...prev, source_name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Reference Number</Label>
                  <Input value={formData.reference_number} onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3} />
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">{format(parseISO(inflow.inflow_date), 'MMM d, yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="font-medium text-emerald-600">+{formatCurrency(inflow.amount, inflow.currency)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Bank Account</p>
                  <p className="font-medium">{inflow.bank_account?.name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Business Unit</p>
                  <p className="font-medium">{BUSINESS_UNIT_LABELS[(inflow as any).business_unit as keyof typeof BUSINESS_UNIT_LABELS] || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Source</p>
                  <p className="font-medium">{inflow.source_name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reference</p>
                  <p className="font-medium">{inflow.reference_number || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cash Flow Type</p>
                  <p className="font-medium capitalize">{inflow.cash_flow_type}</p>
                </div>
                {inflow.description && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Description</p>
                    <p className="font-medium">{inflow.description}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        isDeleting={deleteInflow.isPending}
        title="Delete Cash Inflow"
        description={`Are you sure you want to delete ${inflow.inflow_number}? This action cannot be undone.`}
      />
    </Layout>
  );
};

export default CashInflowDetailPage;
